import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button, Table, Row, Col, Card, CardBody, Input, Badge,
    Pagination, PaginationItem, PaginationLink, FormGroup, Label,
    Modal, ModalHeader, ModalBody, ModalFooter, InputGroup, InputGroupText,
    Form
} from "reactstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import { getCookie } from "../../Helpers/Utils";
import axios from "axios";
import Swal from "sweetalert2";

// MUI DatePicker Imports
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const InvoiceList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // --- Core States ---
    const [invoices, setInvoices] = useState([]);
    const [summary, setSummary] = useState({ total_invoices: 0, total_revenue: 0, total_profit: 0 });
    const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1 });
    const [clients, setClients] = useState([]);

    // --- Payment Modal States ---
    const [paymentModal, setPaymentModal] = useState(false);
    const [selectedInv, setSelectedInv] = useState(null);
    const [payData, setPayData] = useState({ amount: "", mode: "cash", note: "" });

    // --- 🔥 TDS Modal States ---
    const [tdsModal, setTdsModal] = useState(false);
    const [tdsData, setTdsData] = useState({
        invoice_id: "",
        tds_percentage: "0.10",
        tds_amount: "",
        note: "",
        type: "percentage"
    });

    // --- Filter States ---
    const [filters, setFilters] = useState({
        search: "",
        client_id: null,
        from_date: null,
        to_date: null,
        page: 1,
        limit: 10
    });

    const customSelectStyles = {
        control: (base) => ({
            ...base,
            minHeight: '45px',
            height: '45px',
            borderRadius: '8px',
            borderColor: '#dee2e6',
            boxShadow: 'none',
            '&:hover': { borderColor: '#2e4a3d' }
        }),
        valueContainer: (provided) => ({ ...provided, height: '45px', padding: '0 6px' }),
        input: (provided) => ({ ...provided, margin: '0px' }),
        indicatorSeparator: () => ({ display: 'none' }),
        indicatorsContainer: (provided) => ({ ...provided, height: '45px' }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    useEffect(() => {
        fetchClients();
        loadInvoices();
    }, [filters.page, filters.client_id]);

    const fetchClients = async () => {
        dispatch(loadingTrue());
        try {
            const res = await Handler({ method: "GET", url: "/clients/list.php" });
            if (res?.success) {
                // 🔥 Machan, API-la data structure 'c.client.name' nu iruku
                const clientOptions = res.data.map(c => ({
                    value: c.id,
                    label: c.client?.name?.toUpperCase() || "UNKNOWN"
                }));
                setClients(clientOptions);
            } else {
                toast.error("Failed to load clients");
            }
        } catch (err) {
            toast.error("Error loading clients");
        } finally {
            dispatch(loadingFalse());
        }
    };

    const loadInvoices = async () => {
        dispatch(loadingTrue());
        try {
            const params = {
                ...filters,
                client_id: filters.client_id ? filters.client_id.value : "",
                from_date: filters.from_date ? dayjs(filters.from_date).format('YYYY-MM-DD') : "",
                to_date: filters.to_date ? dayjs(filters.to_date).format('YYYY-MM-DD') : ""
            };
            const queryParams = new URLSearchParams(params).toString();
            const res = await Handler({ method: "GET", url: `/invoice/list.php?${queryParams}` });
            console.log("Invoice List Response:", res); // 🔥 Debug Log
            if (res?.success) {
                setInvoices(res.data || []);
                setSummary(res.summary);
                setPagination({
                    current_page: res?.current_page || 1, // 🔥 Structure updated
                    total_pages: res?.total_pages || 1,
                    total_records: res?.total_records || 0
                });
                dispatch(loadingFalse());
            } else {
                toast.error(res?.message || "Failed to fetch invoices");
                dispatch(loadingFalse());
            }
        } catch (err) {
            toast.error("Server connection error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    const handlePrint = async (invoiceId) => {
        // 🔥 Premium Custom HTML Bank Selection machan
        const { value: bankType } = await Swal.fire({
            title: '<span style="font-size: 18px; font-weight: bold; color: #2e4a3d;">PRINT INVOICE</span>',
            html: `
                <p style="font-size: 13px; color: #666; margin-bottom: 20px;">Please select the bank account to display on the PDF.</p>
                <div style="display: flex; flex-direction: column; gap: 12px; padding: 5px;">
                    <label style="display: flex; align-items: center; padding: 15px; border: 2px solid #eee; border-radius: 12px; cursor: pointer; transition: 0.3s;" id="label-indian">
                        <input type="radio" name="bank_choice" value="indian" style="margin-right: 15px; accent-color: #2e4a3d;" checked>
                        <div style="text-align: left;">
                            <div style="font-weight: bold; color: #333;">INDIAN BANK</div>
                            <small style="color: #888;">Acc: 7498025472</small>
                        </div>
                    </label>
                    <label style="display: flex; align-items: center; padding: 15px; border: 2px solid #eee; border-radius: 12px; cursor: pointer; transition: 0.3s;" id="label-union">
                        <input type="radio" name="bank_choice" value="union" style="margin-right: 15px; accent-color: #2e4a3d;">
                        <div style="text-align: left;">
                            <div style="font-weight: bold; color: #333;">UNION BANK OF INDIA</div>
                            <small style="color: #888;">Acc: 393001010999666</small>
                        </div>
                    </label>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: '<i class="bx bx-printer"></i> Generate PDF',
            confirmButtonColor: '#2e4a3d',
            cancelButtonText: 'Cancel',
            cancelButtonColor: '#d33',
            width: '400px',
            padding: '2rem',
            background: '#ffffff',
            borderRadius: '20px',
            customClass: {
                popup: 'animated fadeInDown'
            },
            preConfirm: () => {
                return document.querySelector('input[name="bank_choice"]:checked').value;
            }
        });

        if (bankType) {
            dispatch(loadingTrue());
            const token = getCookie('token');
            try {
                const response = await axios({
                    // url: `http://localhost/cotton-mill/invoice/new.php?id=${invoiceId}&bank_type=${bankType}`,
                    url: `https://muthaiah-cotton.qads.co.in/cotton-mill/invoice/new.php?id=${invoiceId}&bank_type=${bankType}`,
                    method: 'GET',
                    responseType: 'blob',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept': 'application/pdf'
                    }
                });
                const blob = new Blob([response.data], { type: 'application/pdf' });
                const fileURL = URL.createObjectURL(blob);
                const pdfWindow = window.open();
                pdfWindow.location.href = fileURL;
            } catch (err) {
                toast.error("Could not generate PDF.");
            } finally {
                dispatch(loadingFalse());
            }
        }
    };

    // const handlePrint = async (invoiceId) => {
    //     const { value: bankType } = await Swal.fire({
    //         title: '<span style="font-size: 18px; font-weight: bold; color: #2e4a3d;">PRINT INVOICE</span>',
    //         html: `
    //             <p style="font-size: 13px; color: #666; margin-bottom: 20px;">Please select the bank account to display on the PDF.</p>
    //             <div style="display: flex; flex-direction: column; gap: 12px; padding: 5px;">
    //                 <label style="display: flex; align-items: center; padding: 15px; border: 2px solid #eee; border-radius: 12px; cursor: pointer;" id="label-indian">
    //                     <input type="radio" name="bank_choice" value="indian" style="margin-right: 15px; accent-color: #2e4a3d;" checked>
    //                     <div style="text-align: left;">
    //                         <div style="font-weight: bold; color: #333;">INDIAN BANK</div>
    //                         <small style="color: #888;">Acc: 7498025472</small>
    //                     </div>
    //                 </label>
    //                 <label style="display: flex; align-items: center; padding: 15px; border: 2px solid #eee; border-radius: 12px; cursor: pointer;" id="label-union">
    //                     <input type="radio" name="bank_choice" value="union" style="margin-right: 15px; accent-color: #2e4a3d;">
    //                     <div style="text-align: left;">
    //                         <div style="font-weight: bold; color: #333;">UNION BANK OF INDIA</div>
    //                         <small style="color: #888;">Acc: 393001010999666</small>
    //                     </div>
    //                 </label>
    //             </div>
    //     `,
    //         showCancelButton: true,
    //         confirmButtonText: 'Download PDF',
    //         confirmButtonColor: '#2e4a3d',
    //         preConfirm: () => {
    //             return document.querySelector('input[name="bank_choice"]:checked').value;
    //         }
    //     });

    //     if (bankType) {
    //         dispatch(loadingTrue());
    //         const token = getCookie('token');
    //         try {
    //             const response = await axios({
    //                 // 🔥 URL CHANGE: local URL-ah thookitu live-ku direct-ah link pannu
    //                 // url: `https://localhost/cotton-mill/invoice/new.php?id=${invoiceId}&bank_type=${bankType}`,
    //                 url: `https://muthaiah-cotton.qads.co.in/cotton-mill/invoice/new.php?id=${invoiceId}&bank_type=${bankType}`,
    //                 method: 'GET',
    //                 responseType: 'blob', // IMPORTANT
    //                 headers: {
    //                     'Authorization': `Bearer ${token}`,
    //                     'Accept': 'application/pdf'
    //                 }
    //             });

    //             // Browser response handle
    //             const blob = new Blob([response.data], { type: 'application/pdf' });
    //             const url = window.URL.createObjectURL(blob);
    //             const link = document.createElement('a');
    //             link.href = url;
    //             link.setAttribute('download', `Invoice_${invoiceId}.pdf`);
    //             document.body.appendChild(link);
    //             link.click();
    //             link.remove();
    //         } catch (err) {
    //             console.error("PDF Error:", err);
    //             toast.error("Could not generate PDF. Please try again.");
    //         } finally {
    //             dispatch(loadingFalse());
    //         }
    //     }
    // };

    const openPaymentModal = (item) => {
        const balance = item.summary.total_amount - (item.summary.paid_amount || 0);
        setSelectedInv(item);
        setPayData({ amount: balance.toFixed(2), mode: "cash", note: "" });
        setPaymentModal(true);
    };

    const handlePaymentSubmit = async (e) => {
        e.preventDefault();
        const clientId = selectedInv?.invoice?.client_id || selectedInv?.client?.id;
        const invId = selectedInv?.invoice?.id;

        if (!clientId) {
            return toast.error("Valid Client ID is required");
        }

        dispatch(loadingTrue());
        const payload = {
            client_id: clientId,
            invoice_id: invId,
            amount: payData.amount,
            payment_type: "invoice",
            payment_mode: payData.mode,
            note: payData.note
        };
        try {
            const res = await Handler({ method: "POST", url: "/payments/create.php", data: payload });
            if (res?.success) {
                Swal.fire("Success", "Payment recorded successfully!", "success");
                setPaymentModal(false);
                loadInvoices();
                dispatch(loadingFalse());
            } else {
                toast.error(res?.message || "Payment Failed");
                dispatch(loadingFalse());
            }
        } catch (err) {
            toast.error("Server error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    const openTdsModal = (item) => {
        setSelectedInv(item);
        setTdsData({
            invoice_id: item.invoice.id,
            tds_percentage: "0.10",
            tds_amount: (parseFloat(item.summary.total_amount) * 0.001).toFixed(2),
            note: "TDS Deduction",
            type: "percentage"
        });
        setTdsModal(true);
    };

    const handleTdsSubmit = async (e) => {
        e.preventDefault();
        dispatch(loadingTrue());
        const payload = {
            invoice_id: tdsData.invoice_id,
            tds_percentage: tdsData.type === 'percentage' ? tdsData.tds_percentage : null,
            tds_amount: tdsData.type === 'amount' ? tdsData.tds_amount : null,
            note: tdsData.note
        };

        try {
            const res = await Handler({ method: "POST", url: "/tds/create.php", data: payload });
            if (res?.success) {
                Swal.fire("Applied!", "TDS has been recorded.", "success");
                setTdsModal(false);
                dispatch(loadingFalse());
                loadInvoices();
            } else { toast.error(res?.message || "TDS Apply Failed"); dispatch(loadingFalse()); }
        } catch (err) {
            toast.error("TDS Apply Failed");
        } finally {
            dispatch(loadingFalse());
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-1 bg-light min-vh-100">
                <div className="page-header mb-4">
                    <h6 className="text-muted text-uppercase small fw-bold">Sales & Billing</h6>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h3 className="fw-bold m-0 text-theme">Invoice Records</h3>
                            <p className="text-muted small m-0">Detailed list with profit and payment tracking.</p>
                        </div>
                        <Button className="add-btn px-4 shadow-sm" onClick={() => navigate("/invoices/add")}>
                            <i className="bx bx-plus me-1"></i> Create New Invoice
                        </Button>
                    </div>
                </div>

                <Row className="mb-4 g-3">
                    <Col md={4}>
                        <Card className="border-0 shadow-sm rounded-4 bg-theme text-white text-center p-3">
                            <small className="opacity-75 text-uppercase fw-bold">Total Invoices</small>
                            <h2 className="fw-bold mb-0">{summary.total_invoices}</h2>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card className="border-0 shadow-sm rounded-4 text-center p-3 text-primary">
                            <small className="text-muted text-uppercase fw-bold">Total Revenue</small>
                            <h2 className="fw-bold mb-0">₹{summary.total_revenue?.toLocaleString()}</h2>
                        </Card>
                    </Col>
                    {/* 🔥 Added Add-on: Total Profit Card */}
                    <Col md={4}>
                        <Card className="border-0 shadow-sm rounded-4 text-center p-3 text-success">
                            <small className="text-muted text-uppercase fw-bold">Total Profit</small>
                            <h2 className="fw-bold mb-0">₹{summary.total_profit?.toLocaleString()}</h2>
                        </Card>
                    </Col>
                </Row>

                <Card className="border-0 shadow-sm mb-4 rounded-4">
                    <CardBody className="p-4">
                        <Row className="g-3 align-items-end">
                            <Col md={3}>
                                <Label className="small fw-bold">Search</Label>
                                <Input style={{ height: '45px' }} placeholder="No, Client or Sub-Client..." value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })} />
                            </Col>
                            <Col md={3}>
                                <Label className="small fw-bold">Client</Label>
                                <Select
                                    options={clients}
                                    isClearable
                                    placeholder="Select Client"
                                    value={filters.client_id}
                                    styles={customSelectStyles}
                                    onChange={(opt) => setFilters({ ...filters, client_id: opt, page: 1 })}
                                />
                            </Col>
                            <Col md={2}>
                                <Label className="small fw-bold">From</Label>
                                <DatePicker
                                    format="DD/MM/YYYY"
                                    disableFuture
                                    value={filters.from_date}
                                    onChange={(val) => setFilters({ ...filters, from_date: val, page: 1 })}
                                    maxDate={dayjs()}
                                    slotProps={{ textField: { size: 'small', fullWidth: true, sx: { '& .MuiInputBase-root': { height: '45px', borderRadius: '8px' } } } }}
                                />
                            </Col>
                            <Col md={2}>
                                <Label className="small fw-bold">To</Label>
                                <DatePicker
                                    format="DD/MM/YYYY"
                                    disableFuture
                                    value={filters.to_date}
                                    onChange={(val) => setFilters({ ...filters, to_date: val, page: 1 })}
                                    minDate={filters.from_date}
                                    maxDate={dayjs()}
                                    slotProps={{ textField: { size: 'small', fullWidth: true, sx: { '& .MuiInputBase-root': { height: '45px', borderRadius: '8px' } } } }}
                                />
                            </Col>
                            <Col md={2}><Button color="primary" block className="fw-bold" style={{ height: '45px' }} onClick={loadInvoices}><i className="bx bx-filter-alt"></i> Apply</Button></Col>
                        </Row>
                    </CardBody>
                </Card>

                <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                    <CardBody className="p-0">
                        <Table responsive hover className="mb-0 align-middle">
                            <thead className="bg-light text-muted small text-uppercase">
                                <tr>
                                    <th className="ps-4">Invoice Info</th>
                                    <th>Client / Lorry</th>
                                    <th className="text-center">Rate Details</th>
                                    <th className="text-center">Profit</th>
                                    <th className="text-center">Status</th>
                                    <th className="text-end">Final Amount</th>
                                    <th className="text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.length > 0 ? (
                                    invoices.map((item) => {
                                        const total = parseFloat(item.summary.total_amount);
                                        const paid = parseFloat(item.summary.paid_amount || 0);
                                        const balance = total - paid;
                                        const isFullyPaid = balance <= 0.01;

                                        return (
                                            <tr key={item.invoice.id}>
                                                <td className="ps-4">
                                                    <div className="fw-bold text-dark">{item.invoice.invoice_no}</div>
                                                    <small className="text-muted">{dayjs(item.invoice.invoice_date).format('DD-MM-YYYY')}</small>
                                                </td>
                                                <td>
                                                    <div className="fw-bold text-theme">{item.client.name}</div>
                                                    {/* 🔥 Added Add-on: Sub Client Branch Display */}
                                                    {item.sub_client?.name && (
                                                        <div className="small text-muted"><i className="bx bx-git-branch me-1"></i>{item.sub_client.name}</div>
                                                    )}
                                                    <Badge color="light" className="text-dark border small">
                                                        <i className="bx bxs-truck me-1"></i>{item.lorry.lorry_number}
                                                    </Badge>
                                                </td>
                                                <td className="text-center small">
                                                    <div className="text-success">S: ₹{item.rate.selling_rate}</div>
                                                    <div className="text-danger">A: ₹{item.rate.actual_rate}</div>
                                                </td>
                                                <td className="text-center">
                                                    {/* 🔥 Use formatted profit from API */}
                                                    <div className={`fw-bold ${item.financials.profit >= 0 ? 'text-success' : 'text-danger'}`}>
                                                        ₹{item.financials.profit?.toLocaleString()}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    {isFullyPaid ? <Badge color="success" pill style={{ fontSize: '10px', padding: '5px 12px' }}>PAID</Badge> : <Badge color="warning" pill style={{ fontSize: '10px', padding: '5px 12px' }}>DUE</Badge>}
                                                    {paid > 0 && !isFullyPaid && <div className="x-small text-muted mt-1 text-success fw-bold">Recvd: ₹{paid?.toLocaleString()}</div>}
                                                </td>
                                                <td className="text-end pe-4">
                                                    <div className="fw-bold text-dark">₹{total?.toLocaleString()}</div>
                                                    {/* {!isFullyPaid && <div className="text-danger x-small fw-bold">Bal: ₹{balance?.toLocaleString()}</div>} */}
                                                </td>
                                                <td className="text-center">
                                                    <div className="d-flex justify-content-center gap-1">
                                                        {!isFullyPaid && (
                                                            <>
                                                                {/* <Button color="light" size="sm" className="border shadow-sm" onClick={() => openPaymentModal(item)} title="Record Payment"><i className="bx bx-rupee text-success"></i></Button> */}
                                                                <Button color="light" size="sm" className="border shadow-sm" onClick={() => navigate(`/invoices/${item.invoice.id}`)} title="Edit"><i className="bx bx-edit-alt text-info"></i></Button>
                                                            </>
                                                        )}
                                                        <Button color="light" size="sm" className="border shadow-sm" onClick={() => openTdsModal(item)} title="Apply TDS"><i className="bx bx-shield-quarter text-warning"></i></Button>
                                                        <Button color="light" size="sm" className="border shadow-sm" onClick={() => navigate(`/invoices-view/${item.invoice.id}`)} title="View Invoice"><i className="bx bx-show text-warning"></i></Button>
                                                        <Button color="light" size="sm" className="border shadow-sm" onClick={() => handlePrint(item.invoice.id)} title="Print PDF"><i className="bx bx-printer text-primary"></i></Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr><td colSpan="7" className="py-5 text-center text-muted">No records found.</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>

                {pagination?.total_pages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                        <Pagination className="pagination-rounded shadow-sm">
                            {/* Previous Button */}
                            <PaginationItem disabled={filters.page <= 1}>
                                <PaginationLink previous onClick={() => setFilters({ ...filters, page: filters.page - 1 })} />
                            </PaginationItem>

                            {/* Dynamic Page Rendering */}
                            {[...Array(pagination?.total_pages)].map((_, i) => {
                                const pageNumber = i + 1;
                                const isCurrent = pageNumber === filters.page;

                                // Logic to show: First, Last, Current, and neighbors
                                const shouldShow =
                                    pageNumber === 1 ||
                                    pageNumber === pagination?.total_pages ||
                                    (pageNumber >= filters.page - 1 && pageNumber <= filters.page + 1);

                                if (shouldShow) {
                                    return (
                                        <PaginationItem active={isCurrent} key={i}>
                                            <PaginationLink onClick={() => setFilters({ ...filters, page: pageNumber })}>
                                                {pageNumber}
                                            </PaginationLink>
                                        </PaginationItem>
                                    );
                                } else if (
                                    pageNumber === filters.page - 2 ||
                                    pageNumber === filters.page + 2
                                ) {
                                    // Render Ellipsis
                                    return (
                                        <PaginationItem disabled key={i}>
                                            <PaginationLink>...</PaginationLink>
                                        </PaginationItem>
                                    );
                                }
                                return null;
                            })}

                            {/* Next Button */}
                            <PaginationItem disabled={filters.page >= pagination?.total_pages}>
                                <PaginationLink next onClick={() => setFilters({ ...filters, page: filters.page + 1 })} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                )}
            </div>
            {/* Payment & TDS Modals remain exactly as before */}
            <Modal isOpen={paymentModal} toggle={() => setPaymentModal(false)} centered>
                <ModalHeader className="bg-theme text-white border-0 shadow-sm">Record Receipt: {selectedInv?.invoice?.invoice_no}</ModalHeader>
                <Form onSubmit={handlePaymentSubmit}>
                    <ModalBody className="p-4">
                        <div className="bg-light p-3 rounded mb-3 border">
                            <div className="d-flex justify-content-between">
                                <span className="text-muted fw-bold">Party Name</span>
                                <span className="fw-bold">{selectedInv?.client?.name}</span>
                            </div>
                            <div className="d-flex justify-content-between mt-1">
                                <span className="text-muted fw-bold">Pending Balance</span>
                                <span className="fw-bold text-danger">₹ {(selectedInv?.summary?.total_amount - (selectedInv?.summary?.paid_amount || 0))?.toLocaleString()}</span>
                            </div>
                        </div>
                        <FormGroup className="mb-3">
                            <Label className="small fw-bold text-muted">Amount Received (₹)</Label>
                            <InputGroup size="lg"><InputGroupText className="bg-white">₹</InputGroupText><Input type="number" step="any" required value={payData.amount} onChange={(e) => setPayData({ ...payData, amount: e.target.value })} /></InputGroup>
                        </FormGroup>
                        <FormGroup className="mb-3">
                            <Label className="small fw-bold text-muted">Payment Mode</Label>
                            <Input type="select" className="rounded-3" value={payData.mode} onChange={(e) => setPayData({ ...payData, mode: e.target.value })}>
                                <option value="cash">Cash Collection</option><option value="bank">Bank / UPI Transfer</option>
                            </Input>
                        </FormGroup>
                        <FormGroup><Label className="small fw-bold text-muted">Note / Reference No</Label><Input type="textarea" placeholder="Remarks..." value={payData.note} onChange={(e) => setPayData({ ...payData, note: e.target.value })} /></FormGroup>
                    </ModalBody>
                    <ModalFooter className="border-0">
                        <Button color="secondary" outline className="rounded-3" onClick={() => setPaymentModal(false)}>Cancel</Button>
                        <Button color="success" type="submit" className="rounded-3 fw-bold shadow-sm px-4">Confirm Payment</Button>
                    </ModalFooter>
                </Form>
            </Modal>

            <Modal isOpen={tdsModal} toggle={() => setTdsModal(false)} centered size="sm">
                <ModalHeader className="bg-warning text-dark border-0">Apply TDS: {selectedInv?.invoice?.invoice_no}</ModalHeader>
                <Form onSubmit={handleTdsSubmit}>
                    <ModalBody className="p-4">
                        <div className="d-flex mb-3 p-1 bg-light rounded-pill border">
                            <Button type="button" color={tdsData.type === "percentage" ? "warning" : "light"} className="flex-fill rounded-pill border-0 py-1 btn-sm fw-bold" onClick={() => setTdsData({ ...tdsData, type: "percentage" })}>By %</Button>
                            <Button type="button" color={tdsData.type === "amount" ? "warning" : "light"} className="flex-fill rounded-pill border-0 py-1 btn-sm fw-bold" onClick={() => setTdsData({ ...tdsData, type: "amount" })}>By Amount</Button>
                        </div>
                        {tdsData.type === "percentage" ? (
                            <FormGroup className="mb-3">
                                <Label className="small fw-bold">Percentage (%)</Label>
                                <InputGroup><Input type="number" value={tdsData.tds_percentage} onChange={(e) => {
                                    const pct = e.target.value;
                                    const amt = (parseFloat(selectedInv?.summary.total_amount) * (pct / 100)).toFixed(2);
                                    setTdsData({ ...tdsData, tds_percentage: pct, tds_amount: amt });
                                }} /><InputGroupText>%</InputGroupText></InputGroup>
                            </FormGroup>
                        ) : (
                            <FormGroup className="mb-3">
                                <Label className="small fw-bold">Amount (₹)</Label>
                                <InputGroup><InputGroupText>₹</InputGroupText><Input type="number" value={tdsData.tds_amount} onChange={(e) => setTdsData({ ...tdsData, tds_amount: e.target.value })} /></InputGroup>
                            </FormGroup>
                        )}
                        <div className="text-center p-2 bg-light rounded"><small className="text-muted">Est. Deduction: </small><h5 className="text-danger fw-bold">₹{parseFloat(tdsData.tds_amount || 0)?.toLocaleString()}</h5></div>
                        <FormGroup className="mt-2"><Label className="small fw-bold">Reason</Label><Input type="text" placeholder="TDS Note..." value={tdsData.note} onChange={(e) => setTdsData({ ...tdsData, note: e.target.value })} /></FormGroup>
                    </ModalBody>
                    <ModalFooter className="border-0">
                        <Button color="secondary" outline size="sm" onClick={() => setTdsModal(false)}>Cancel</Button>
                        <Button color="warning" type="submit" size="sm" className="fw-bold">Apply Deduction</Button>
                    </ModalFooter>
                </Form>
            </Modal>
        </LocalizationProvider>
    );
};

export default InvoiceList;